// Canvas描画関連
import { albumImages, currentSelectedTemplate, imageTransforms, editingImageIndex, tempTransform, editingImage, setEditingState, clearEditingState } from "./state.js";
import { drawAllTextElements } from "./textRenderer.js";

// ポップアップCanvasに画像を描画する関数
export const drawImageToPopupCanvas = (popupCtx, popupCanvas, imageDataUrl, imageIndexForErrorMessage) => {
  if (!popupCtx || !popupCanvas) return;

  popupCtx.clearRect(0, 0, popupCanvas.width, popupCanvas.height);

  // 編集中の画像インデックスを設定
  const editingIndex = imageIndexForErrorMessage - 1;

  // 現在の変換情報をコピー（デバッグ情報追加）
  console.log(`[drawImageToPopupCanvas] 画像 ${imageIndexForErrorMessage} の編集開始`);
  console.log(`[drawImageToPopupCanvas] 保存されている変換情報:`, JSON.stringify(imageTransforms[editingIndex]));
  const tempTransformCopy = { ...imageTransforms[editingIndex] };
  console.log(`[drawImageToPopupCanvas] tempTransformにコピー:`, JSON.stringify(tempTransformCopy));

  // 現在のテンプレートのスロット情報を取得
  const currentSlot = currentSelectedTemplate ? currentSelectedTemplate.photoSlots[editingIndex] : null;

  const img = new Image();
  img.onload = () => {
    // 編集状態を設定
    setEditingState(editingIndex, tempTransformCopy, img);

    // 画像を描画する関数
    const drawEditingImage = () => {
      popupCtx.clearRect(0, 0, popupCanvas.width, popupCanvas.height);

      const canvasWidth = popupCanvas.width;
      const canvasHeight = popupCanvas.height;

      if (!currentSlot) {
        // テンプレート未選択時は従来の描画
        const imgAspectRatio = img.width / img.height;
        const canvasAspectRatio = canvasWidth / canvasHeight;
        let baseWidth, baseHeight;

        if (imgAspectRatio > canvasAspectRatio) {
          baseWidth = canvasWidth * 0.8;
          baseHeight = baseWidth / imgAspectRatio;
        } else {
          baseHeight = canvasHeight * 0.8;
          baseWidth = baseHeight * imgAspectRatio;
        }

        popupCtx.save();
        const centerX = canvasWidth / 2;
        const centerY = canvasHeight / 2;
        popupCtx.translate(centerX, centerY);
        popupCtx.rotate((tempTransform.rotation * Math.PI) / 180);
        popupCtx.scale(tempTransform.scale, tempTransform.scale);

        const drawX = -baseWidth / 2 + tempTransform.offsetX;
        const drawY = -baseHeight / 2 + tempTransform.offsetY;

        popupCtx.drawImage(img, drawX, drawY, baseWidth, baseHeight);
        popupCtx.restore();

        // 枠線を描画
        popupCtx.strokeStyle = "#ccc";
        popupCtx.lineWidth = 1;
        popupCtx.strokeRect(0, 0, canvasWidth, canvasHeight);
        return;
      }

      // テンプレートのスロット情報に基づいて描画
      const slot = currentSlot.targetSlot;

      // スロットのアスペクト比を計算
      const slotAspectRatio = slot.width / slot.height;

      // キャンバス内でのスロットサイズを計算（80%のマージンを確保）
      let displaySlotWidth, displaySlotHeight;
      const maxWidth = canvasWidth * 0.8;
      const maxHeight = canvasHeight * 0.8;

      if (slotAspectRatio > maxWidth / maxHeight) {
        displaySlotWidth = maxWidth;
        displaySlotHeight = maxWidth / slotAspectRatio;
      } else {
        displaySlotHeight = maxHeight;
        displaySlotWidth = maxHeight * slotAspectRatio;
      }

      // スロットの中心位置
      const slotCenterX = canvasWidth / 2;
      const slotCenterY = canvasHeight / 2;

      // 全体の座標系を保存
      popupCtx.save();

      // スロットの回転を適用
      popupCtx.translate(slotCenterX, slotCenterY);
      popupCtx.rotate((slot.angle * Math.PI) / 180);

      // 枠線の太さ（1.5mm相当をピクセルに変換）
      const borderWidth = (displaySlotWidth / slot.width) * 1.5;

      // 1. スロットの背景を描画（白い背景とドロップシャドウ）
      popupCtx.shadowColor = "rgba(0, 0, 0, 0.5)";
      popupCtx.shadowBlur = 5;
      popupCtx.shadowOffsetX = 2;
      popupCtx.shadowOffsetY = 2;

      // 白い背景
      popupCtx.fillStyle = "white";
      popupCtx.fillRect(-displaySlotWidth / 2, -displaySlotHeight / 2, displaySlotWidth, displaySlotHeight);

      // シャドウをリセット
      popupCtx.shadowColor = "transparent";
      popupCtx.shadowBlur = 0;
      popupCtx.shadowOffsetX = 0;
      popupCtx.shadowOffsetY = 0;

      // 画像のアスペクト比とCoverフィット計算
      const imgAspectRatio = img.width / img.height;
      const innerWidth = displaySlotWidth - borderWidth * 2;
      const innerHeight = displaySlotHeight - borderWidth * 2;
      const innerAspectRatio = innerWidth / innerHeight;

      let baseImgWidth, baseImgHeight;
      if (imgAspectRatio > innerAspectRatio) {
        baseImgHeight = innerHeight;
        baseImgWidth = baseImgHeight * imgAspectRatio;
      } else {
        baseImgWidth = innerWidth;
        baseImgHeight = baseImgWidth / imgAspectRatio;
      }

      // 画像の描画位置
      const imgX = -baseImgWidth / 2 + tempTransform.offsetX;
      const imgY = -baseImgHeight / 2 + tempTransform.offsetY;

      // 2. 画像全体を半透明で描画（はみ出し部分を見せるため）
      popupCtx.save();
      popupCtx.rotate((tempTransform.rotation * Math.PI) / 180);
      popupCtx.scale(tempTransform.scale, tempTransform.scale);
      popupCtx.globalAlpha = 0.3;
      popupCtx.drawImage(img, imgX, imgY, baseImgWidth, baseImgHeight);
      popupCtx.restore();

      // 3. クリッピング領域を設定（枠線の内側）
      popupCtx.save();
      popupCtx.beginPath();
      popupCtx.rect(-displaySlotWidth / 2 + borderWidth, -displaySlotHeight / 2 + borderWidth, displaySlotWidth - borderWidth * 2, displaySlotHeight - borderWidth * 2);
      popupCtx.clip();

      // 4. クリッピング領域内に画像を不透明で描画
      popupCtx.rotate((tempTransform.rotation * Math.PI) / 180);
      popupCtx.scale(tempTransform.scale, tempTransform.scale);
      popupCtx.globalAlpha = 1.0;
      popupCtx.drawImage(img, imgX, imgY, baseImgWidth, baseImgHeight);
      popupCtx.restore(); // クリッピングをリセット

      // 5. 枠線を描画
      // 外枠（赤色で見やすく）
      popupCtx.strokeStyle = "#ff0000";
      popupCtx.lineWidth = 2;
      popupCtx.strokeRect(-displaySlotWidth / 2, -displaySlotHeight / 2, displaySlotWidth, displaySlotHeight);

      // 内側の枠線（実際の画像表示エリア - 青い点線）
      popupCtx.strokeStyle = "#0000ff";
      popupCtx.lineWidth = 1;
      popupCtx.setLineDash([5, 5]);
      popupCtx.strokeRect(-displaySlotWidth / 2 + borderWidth, -displaySlotHeight / 2 + borderWidth, displaySlotWidth - borderWidth * 2, displaySlotHeight - borderWidth * 2);
      popupCtx.setLineDash([]);

      popupCtx.restore(); // 全体の座標系を復元
    };

    // 初回描画
    drawEditingImage();

    // 描画関数を保存（ボタン操作時に使用）
    popupCanvas.drawEditingImage = drawEditingImage;
  };

  img.onerror = () => {
    console.error("ポップアップCanvasへの画像読み込み失敗:", imageDataUrl.substring(0, 100) + "...");
    const modalErrorMessage = document.getElementById("modalErrorMsg") || document.createElement("p");
    modalErrorMessage.id = "modalErrorMsg";
    modalErrorMessage.textContent = `画像 ${imageIndexForErrorMessage} のプレビュー表示に失敗しました。`;
    modalErrorMessage.style.color = "red";
    popupCanvas.parentElement.appendChild(modalErrorMessage);
  };

  img.src = imageDataUrl;
};

// メインアルバムCanvasを描画する関数
export const drawMainAlbumCanvas = (mainCtx, mainAlbumCanvas, mainAlbumCanvasMessage) => {
  if (!mainCtx || !mainAlbumCanvas) return;

  // テンプレート未選択時の処理
  if (!currentSelectedTemplate) {
    mainCtx.clearRect(0, 0, mainAlbumCanvas.width, mainAlbumCanvas.height);
    if (mainAlbumCanvasMessage) {
      mainAlbumCanvasMessage.textContent = "まずテンプレートを選択してください。";
    }
    console.log("[drawMainAlbumCanvas] Template not selected.");
    return;
  }

  console.log("[drawMainAlbumCanvas] Called. Selected Template:", currentSelectedTemplate.templateId, "Current albumImages:", JSON.parse(JSON.stringify(albumImages)));
  mainCtx.clearRect(0, 0, mainAlbumCanvas.width, mainAlbumCanvas.height);
  if (mainAlbumCanvasMessage) mainAlbumCanvasMessage.textContent = ""; // メッセージをクリア

  // 背景画像を描画
  const backgroundImage = new Image();

  // 背景画像のパスを設定（ページサイズに応じた共通画像）
  const pageSize = currentSelectedTemplate.pageSize.toLowerCase();
  backgroundImage.src = `/assets/album_templates/${pageSize}/${pageSize}_base.png`;

  // テキスト要素を描画（背景画像読み込み後に実行）
  backgroundImage.onload = () => {
    // 背景画像をCanvasサイズに合わせて描画
    mainCtx.drawImage(backgroundImage, 0, 0, mainAlbumCanvas.width, mainAlbumCanvas.height);

    // zIndexでソート（小さい値が先に描画される = 奥になる）
    const sortedSlots = [...currentSelectedTemplate.photoSlots].sort((a, b) => a.zIndex - b.zIndex);

    // 各写真スロットを描画
    sortedSlots.forEach((slot) => {
      // photoSlotsの配列インデックスを取得（元の順序でのインデックス）
      const slotIndex = currentSelectedTemplate.photoSlots.indexOf(slot);
      const imgData = albumImages[slotIndex];

      if (imgData && imgData.src) {
        console.log(`[drawMainAlbumCanvas] Drawing image ${slotIndex + 1} with zIndex ${slot.zIndex}`);
        const img = new Image();
        img.onload = () => {
          mainCtx.save(); // 描画状態を保存

          // ページサイズに応じた寸法を取得
          let pageWidthMM, pageHeightMM;
          if (currentSelectedTemplate.pageSize.toLowerCase() === "b5") {
            pageWidthMM = 182; // B5縦向き
            pageHeightMM = 257;
          } else if (currentSelectedTemplate.pageSize.toLowerCase() === "a4") {
            pageWidthMM = 170; // A4横向き
            pageHeightMM = 120;
          } else {
            // デフォルトはB5
            pageWidthMM = 182;
            pageHeightMM = 257;
          }

          const canvasWidth = mainAlbumCanvas.width;
          const canvasHeight = mainAlbumCanvas.height;

          // アスペクト比を保ちながらスケールを計算
          const scaleX = canvasWidth / pageWidthMM;
          const scaleY = canvasHeight / pageHeightMM;
          const scale = Math.min(scaleX, scaleY); // アスペクト比を保つため小さい方を使用

          // 実際の描画サイズ
          const actualWidth = pageWidthMM * scale;
          const actualHeight = pageHeightMM * scale;

          // センタリングのためのオフセット
          const offsetX = (canvasWidth - actualWidth) / 2;
          const offsetY = (canvasHeight - actualHeight) / 2;

          const scaledX = offsetX + slot.targetSlot.x * scale;
          const scaledY = offsetY + slot.targetSlot.y * scale;
          const scaledWidth = slot.targetSlot.width * scale;
          const scaledHeight = slot.targetSlot.height * scale;
          const scaledCenterX = scaledX + scaledWidth / 2;
          const scaledCenterY = scaledY + scaledHeight / 2;

          // ドロップシャドウを描画
          mainCtx.shadowColor = "rgba(0, 0, 0, 0.5)";
          mainCtx.shadowBlur = 1.764 * scale; // 1.764mm
          mainCtx.shadowOffsetX = 0.354 * scale; // 0.354mm
          mainCtx.shadowOffsetY = 0.354 * scale; // 0.354mm

          mainCtx.translate(scaledCenterX, scaledCenterY);
          mainCtx.rotate(-(slot.targetSlot.angle * Math.PI) / 180); // 回転 (度からラジアンへ変換)
          mainCtx.translate(-scaledCenterX, -scaledCenterY); // 座標系を元に戻す

          // 白い背景を描画（写真の下地として）
          mainCtx.fillStyle = "white";
          mainCtx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);

          // シャドウをリセット（写真自体にはシャドウを適用しない）
          mainCtx.shadowColor = "transparent";
          mainCtx.shadowBlur = 0;
          mainCtx.shadowOffsetX = 0;
          mainCtx.shadowOffsetY = 0;

          // 枠線の太さを考慮したクリッピング領域
          const borderWidth = 1.5 * scale; // 1.5mm
          const innerX = scaledX + borderWidth;
          const innerY = scaledY + borderWidth;
          const innerWidth = scaledWidth - borderWidth * 2;
          const innerHeight = scaledHeight - borderWidth * 2;

          // クリッピングパスを作成
          mainCtx.beginPath();
          mainCtx.rect(innerX, innerY, innerWidth, innerHeight);
          mainCtx.clip();

          // Coverフィットの計算と描画
          const slotAspectRatio = innerWidth / innerHeight;
          const imgAspectRatio = img.width / img.height;
          let drawWidth, drawHeight, drawX, drawY;

          if (imgAspectRatio > slotAspectRatio) {
            // 画像がスロットより横長
            drawHeight = innerHeight;
            drawWidth = imgAspectRatio * drawHeight;
            drawX = innerX - (drawWidth - innerWidth) / 2; // 中央寄せ
            drawY = innerY;
          } else {
            // 画像がスロットより縦長または同じ
            drawWidth = innerWidth;
            drawHeight = drawWidth / imgAspectRatio;
            drawX = innerX;
            drawY = innerY - (drawHeight - innerHeight) / 2; // 中央寄せ
          }

          // 画像の変換情報を適用
          const transform = imageTransforms[slotIndex];
          if (transform) {
            // 回転を考慮したCoverフィットの再計算
            const imageRotation = (transform.rotation * Math.PI) / 180;

            // 枠の四隅の座標（中心が原点）
            const frameCorners = [
              { x: -innerWidth / 2, y: -innerHeight / 2 }, // 左上
              { x: innerWidth / 2, y: -innerHeight / 2 }, // 右上
              { x: innerWidth / 2, y: innerHeight / 2 }, // 右下
              { x: -innerWidth / 2, y: innerHeight / 2 }, // 左下
            ];

            // 各コーナーが画像内に収まるために必要な最小スケールを計算
            let maxRequiredScale = 1;

            frameCorners.forEach((corner) => {
              // コーナーの座標を画像の座標系に変換（逆回転）
              const rotatedCornerX = corner.x * Math.cos(-imageRotation) - corner.y * Math.sin(-imageRotation);
              const rotatedCornerY = corner.x * Math.sin(-imageRotation) + corner.y * Math.cos(-imageRotation);

              // このコーナーが画像内に収まるために必要なスケール
              const requiredScaleX = Math.abs(rotatedCornerX) / (drawWidth / 2);
              const requiredScaleY = Math.abs(rotatedCornerY) / (drawHeight / 2);
              const requiredScale = Math.max(requiredScaleX, requiredScaleY);

              maxRequiredScale = Math.max(maxRequiredScale, requiredScale);
            });

            // 実際に適用するスケール（ユーザー設定と最小要求の最大値）
            const effectiveScale = Math.max(transform.scale, maxRequiredScale);

            // 画像の中心を基準に変換を適用
            const imageCenterX = innerX + innerWidth / 2;
            const imageCenterY = innerY + innerHeight / 2;

            mainCtx.save();
            mainCtx.translate(imageCenterX, imageCenterY);
            mainCtx.rotate(imageRotation);
            mainCtx.scale(effectiveScale, effectiveScale);

            // 個別編集画面と全体プレビューの座標系変換
            // 個別編集画面でのベース画像サイズを計算
            const slot = currentSelectedTemplate.photoSlots[slotIndex].targetSlot;
            const slotAspectRatio = slot.width / slot.height;

            // 個別編集画面での表示サイズ（popupCanvasサイズ: 500x400）
            const popupCanvasWidth = 500;
            const popupCanvasHeight = 400;
            const maxPopupWidth = popupCanvasWidth * 0.8;
            const maxPopupHeight = popupCanvasHeight * 0.8;

            let popupDisplaySlotWidth, popupDisplaySlotHeight;
            if (slotAspectRatio > maxPopupWidth / maxPopupHeight) {
              popupDisplaySlotWidth = maxPopupWidth;
              popupDisplaySlotHeight = maxPopupWidth / slotAspectRatio;
            } else {
              popupDisplaySlotHeight = maxPopupHeight;
              popupDisplaySlotWidth = maxPopupHeight * slotAspectRatio;
            }

            const popupBorderWidth = (popupDisplaySlotWidth / slot.width) * 1.5;
            const popupInnerWidth = popupDisplaySlotWidth - popupBorderWidth * 2;
            const popupInnerHeight = popupDisplaySlotHeight - popupBorderWidth * 2;
            const popupInnerAspectRatio = popupInnerWidth / popupInnerHeight;

            let popupBaseImgWidth, popupBaseImgHeight;
            if (imgAspectRatio > popupInnerAspectRatio) {
              popupBaseImgHeight = popupInnerHeight;
              popupBaseImgWidth = popupBaseImgHeight * imgAspectRatio;
            } else {
              popupBaseImgWidth = popupInnerWidth;
              popupBaseImgHeight = popupBaseImgWidth / imgAspectRatio;
            }

            // 座標系変換比率を計算
            const scaleRatioX = drawWidth / popupBaseImgWidth;
            const scaleRatioY = drawHeight / popupBaseImgHeight;

            // 変換後の描画位置を計算
            const transformedDrawX = -drawWidth / 2 + transform.offsetX * scaleRatioX;
            const transformedDrawY = -drawHeight / 2 + transform.offsetY * scaleRatioY;

            mainCtx.drawImage(img, transformedDrawX, transformedDrawY, drawWidth, drawHeight);
            mainCtx.restore();
          } else {
            // 変換情報がない場合は通常描画
            mainCtx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          }

          mainCtx.restore(); // 描画状態を復元 (クリッピングも解除)
        };
        img.onerror = () => console.error(`Main canvas: Image ${slotIndex + 1} load error for src: ${imgData.src.substring(0, 100)}`);
        img.src = imgData.src;
      } else {
        // 画像がないスロットの描画（プレースホルダー）
        drawPlaceholderSlot(mainCtx, mainAlbumCanvas, slot, slotIndex);
      }
    });

    // 全ての画像描画が完了した後にテキストを描画
    setTimeout(async () => {
      drawAllTextElements(mainCtx, mainAlbumCanvas, currentSelectedTemplate);
    }, 100);
  };

  backgroundImage.onerror = () => {
    console.error(`Failed to load background image for template: ${currentSelectedTemplate.templateId}`);
    if (mainAlbumCanvasMessage) {
      mainAlbumCanvasMessage.textContent = "テンプレート背景画像の読み込みに失敗しました。";
    }
  };
};

// プレースホルダースロットを描画する関数
const drawPlaceholderSlot = (mainCtx, mainAlbumCanvas, slot, slotIndex) => {
  mainCtx.save();

  // Canvas座標系に変換（mm → px）
  // ページサイズに応じた寸法を取得
  let pageWidthMM, pageHeightMM;
  if (currentSelectedTemplate.pageSize.toLowerCase() === "b5") {
    pageWidthMM = 182; // B5縦向き
    pageHeightMM = 257;
  } else if (currentSelectedTemplate.pageSize.toLowerCase() === "a4") {
    pageWidthMM = 170; // A4横向き
    pageHeightMM = 120;
  } else {
    // デフォルトはB5
    pageWidthMM = 182;
    pageHeightMM = 257;
  }

  const canvasWidth = mainAlbumCanvas.width;
  const canvasHeight = mainAlbumCanvas.height;

  const scaleX = canvasWidth / pageWidthMM;
  const scaleY = canvasHeight / pageHeightMM;
  const scale = Math.min(scaleX, scaleY);

  const actualWidth = pageWidthMM * scale;
  const actualHeight = pageHeightMM * scale;
  const offsetX = (canvasWidth - actualWidth) / 2;
  const offsetY = (canvasHeight - actualHeight) / 2;

  const scaledX = offsetX + slot.targetSlot.x * scale;
  const scaledY = offsetY + slot.targetSlot.y * scale;
  const scaledWidth = slot.targetSlot.width * scale;
  const scaledHeight = slot.targetSlot.height * scale;
  const scaledCenterX = scaledX + scaledWidth / 2;
  const scaledCenterY = scaledY + scaledHeight / 2;

  mainCtx.translate(scaledCenterX, scaledCenterY);
  mainCtx.rotate(-(slot.targetSlot.angle * Math.PI) / 180);
  mainCtx.translate(-scaledCenterX, -scaledCenterY);

  // プレースホルダーの背景
  mainCtx.fillStyle = "#f0f0f0";
  mainCtx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);

  // プレースホルダーの枠線
  mainCtx.strokeStyle = "#ccc";
  mainCtx.lineWidth = 1;
  mainCtx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);

  // プレースホルダーのテキスト
  mainCtx.fillStyle = "#999";
  mainCtx.font = "14px sans-serif";
  mainCtx.textAlign = "center";
  mainCtx.textBaseline = "middle";
  mainCtx.fillText(`写真${slotIndex + 1}`, scaledCenterX, scaledCenterY);

  mainCtx.restore();
  console.log(`[drawMainAlbumCanvas] No image for slot index ${slotIndex}`);
};

// クリック位置から画像インデックスを取得する関数
export const getClickedImageIndex = (clickX, clickY, mainAlbumCanvas) => {
  if (!currentSelectedTemplate) return null;

  // Canvasの実際のサイズと表示サイズの比率を計算
  const rect = mainAlbumCanvas.getBoundingClientRect();
  const scaleFactorX = mainAlbumCanvas.width / rect.width;
  const scaleFactorY = mainAlbumCanvas.height / rect.height;

  // クリック座標をCanvas座標系に変換
  const canvasClickX = clickX * scaleFactorX;
  const canvasClickY = clickY * scaleFactorY;

  console.log(`[getClickedImageIndex] 表示サイズ: ${rect.width}x${rect.height}, Canvas実サイズ: ${mainAlbumCanvas.width}x${mainAlbumCanvas.height}`);
  console.log(`[getClickedImageIndex] スケール比率: ${scaleFactorX.toFixed(3)}x${scaleFactorY.toFixed(3)}`);
  console.log(`[getClickedImageIndex] クリック位置: 表示(${clickX}, ${clickY}) → Canvas(${canvasClickX}, ${canvasClickY})`);

  // Canvas座標系に変換（mm → px）
  let pageWidthMM, pageHeightMM;
  if (currentSelectedTemplate.pageSize.toLowerCase() === "b5") {
    pageWidthMM = 182;
    pageHeightMM = 257;
  } else if (currentSelectedTemplate.pageSize.toLowerCase() === "a4") {
    pageWidthMM = 170;
    pageHeightMM = 120;
  } else {
    pageWidthMM = 182;
    pageHeightMM = 257;
  }

  const canvasWidth = mainAlbumCanvas.width;
  const canvasHeight = mainAlbumCanvas.height;

  const scaleX = canvasWidth / pageWidthMM;
  const scaleY = canvasHeight / pageHeightMM;
  const scale = Math.min(scaleX, scaleY);

  const actualWidth = pageWidthMM * scale;
  const actualHeight = pageHeightMM * scale;
  const offsetX = (canvasWidth - actualWidth) / 2;
  const offsetY = (canvasHeight - actualHeight) / 2;

  // zIndexの降順でチェック（手前の画像から判定）
  const sortedSlots = [...currentSelectedTemplate.photoSlots].map((slot, index) => ({ slot, index })).sort((a, b) => b.slot.zIndex - a.slot.zIndex);

  // 各スロットの位置をチェック
  for (const { slot, index } of sortedSlots) {
    const scaledX = offsetX + slot.targetSlot.x * scale;
    const scaledY = offsetY + slot.targetSlot.y * scale;
    const scaledWidth = slot.targetSlot.width * scale;
    const scaledHeight = slot.targetSlot.height * scale;
    const scaledCenterX = scaledX + scaledWidth / 2;
    const scaledCenterY = scaledY + scaledHeight / 2;

    // 回転を考慮したクリック判定
    if (slot.targetSlot.angle !== 0) {
      // クリック位置をスロットの中心を基準とした座標系に変換
      const relativeX = canvasClickX - scaledCenterX;
      const relativeY = canvasClickY - scaledCenterY;

      // スロットの回転角度（ラジアン）
      const angleRad = (-slot.targetSlot.angle * Math.PI) / 180; // 逆回転で元の座標系に戻す

      // クリック位置を回転前の座標系に変換
      const rotatedX = relativeX * Math.cos(angleRad) - relativeY * Math.sin(angleRad);
      const rotatedY = relativeX * Math.sin(angleRad) + relativeY * Math.cos(angleRad);

      // 回転前の矩形内かチェック
      if (Math.abs(rotatedX) <= scaledWidth / 2 && Math.abs(rotatedY) <= scaledHeight / 2) {
        console.log(`[getClickedImageIndex] 回転スロット ${index} にヒット (角度: ${slot.targetSlot.angle}°)`);
        console.log(`[getClickedImageIndex] 相対座標: (${relativeX.toFixed(1)}, ${relativeY.toFixed(1)}) → 回転後: (${rotatedX.toFixed(1)}, ${rotatedY.toFixed(1)})`);
        return index;
      }
    } else {
      // 回転なしの場合は通常の矩形判定
      if (canvasClickX >= scaledX && canvasClickX <= scaledX + scaledWidth && canvasClickY >= scaledY && canvasClickY <= scaledY + scaledHeight) {
        console.log(`[getClickedImageIndex] 通常スロット ${index} にヒット`);
        return index;
      }
    }

    console.log(`[getClickedImageIndex] スロット ${index} 判定: 中心(${scaledCenterX.toFixed(1)}, ${scaledCenterY.toFixed(1)}), サイズ(${scaledWidth.toFixed(1)}x${scaledHeight.toFixed(1)}), 角度: ${slot.targetSlot.angle}°`);
  }

  console.log(`[getClickedImageIndex] どのスロットにもヒットしませんでした`);
  return null;
};
